import { DefaultError, useMutation, useQuery } from '@tanstack/react-query';
import { useQueryToast } from 'hooks';
import i18next from 'i18next';
import { useNavigate } from 'react-router-dom';
import { useActions } from 'store/state';
import { apiClient } from 'utils';
import { useResponseHandler } from 'utils/service/responseHandler';
import { warningToast } from 'utils/toast/warningToast';
import {
  IResendTwoFactor,
  ITwoFactorStatus,
  LoginResponse,
  LoginTwoFactorMutation,
} from './types';
import { useStoreState } from 'providers/StoreStateContext';

export const useTwoFactor = () => {
  const { tokenData } = useStoreState();
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useTwoFactor');
  return useMutation<any, DefaultError>({
    async mutationFn() {
      loading(i18next.t('service-message.auth.enable-two-factor.loading'));
      const body = { userId: tokenData?.UserId };
      const response = await apiClient({
        method: 'POST',
        url: 'Auth/EnableTwoFactor',
        body,
      });

      return responseHandler(response, { groupName: 'useTwoFactor' });
    },
    onError: dismiss,
    onSuccess: () =>
      success(i18next.t('service-message.auth.enable-two-factor.success')),
  });
};

export const useLoginTwoFactor = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useLoginTwoFactor');
  const { setToken, setRefreshToken, setTwoFactorTokenExpiresAt, setIsTokenStale } =
    useActions();
  return useMutation<any, DefaultError, LoginTwoFactorMutation>({
    async mutationFn(body) {
      loading(i18next.t('service-message.auth.login-two-factor.loading'));
      const response = await apiClient({
        method: 'POST',
        url: 'Auth/LoginTwoFactor',
        body,
      });

      return responseHandler(response, { groupName: 'useLoginTwoFactor' });
    },
    onError: dismiss,
    onSuccess: data => {
      success(i18next.t('service-message.auth.login-two-factor.success'));
         setIsTokenStale(true);
      setToken(data.token);
      setRefreshToken(data.refreshToken);
      setTwoFactorTokenExpiresAt(null);
      if (data.isPasswordChangeRequired) {
        warningToast('toast-message.force-change-password');
      }
    },
  });
};

export const useResendTwoFactor = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useResendTwoFactor');
  const navigate = useNavigate();
  const { setTwoFactorTokenExpiresAt } = useActions();
  return useMutation<LoginResponse, DefaultError, IResendTwoFactor>({
    async mutationFn(body) {
      loading(i18next.t('service-message.auth.resend-two-factor.loading'));
      const response = await apiClient({
        method: 'POST',
        url: 'Auth/ResendTwoFactor',
        body,
      });

      return responseHandler(response, { groupName: 'useResendTwoFactor' });
    },
    onError: dismiss,
    onSuccess: (data, variables) => {
      const urlWithParams = `/two-factor/${data.token2FA}/${variables.email}`;
      setTwoFactorTokenExpiresAt(data.token2FAExpires);
      navigate(urlWithParams);
      success(i18next.t('service-message.auth.resend-two-factor.success'));
      return;
    },
  });
};

export const useGetTwoFactorStatus = (userId?: string, enabled?: boolean) => {
  const { responseHandler } = useResponseHandler();
  
  return useQuery<ITwoFactorStatus, DefaultError>({
    queryKey: ['two-factor-status', userId],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: 'Auth/TwoFactorStatus',
        params: userId ? { userId } : undefined,
      });

      return responseHandler(response, {
        groupName: 'useGetTwoFactorStatus',
      });
    },
    enabled: enabled !== false && !!userId,
    retry: false,
  });
};
